import { makeStyles, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot } from "@fortawesome/free-solid-svg-icons"

import { colors } from "../../../utils/theme"
import { RelapsePreventionPlaces } from "../../../hooks/useRelapsePreventionPlaces"
import { Contact, Place, SelectedOption } from "../../../utils/safetyPlanUtils"

interface SafetyPlanSelectedOptionValueProps {
  option: SelectedOption
  activeRelapsePreventionPlaces?: RelapsePreventionPlaces
}

/**
 * Renders a single selected response option from a client's safety plan in a
 * read-only display format.
 *
 * The rendered output varies based on `option.category_type`:
 * - **`contact`** — lists each saved contact as a row showing the full name
 *   and phone number.
 * - **`place`** — lists each saved place name with a location pin icon.
 * - **`text_box`** — lists each free-text string the client entered.
 * - **`radio`** — renders a Yes/No toggle pill (styled after a segmented
 *   control). The selected state is determined by whether the option id is
 *   `"keepingMyselfSafe"` (the injected placeholder, indicating "No") or a
 *   real option id (indicating "Yes").
 * - **`risky_place/safe_place`** — renders one highlighted place card per
 *   active `relapse_prevention_places` row linked to the selected response
 *   option id, using the stored place category as the card label.
 * - **`text_area`** — renders one or more paragraph-style text cards using
 *   the saved free-text values.
 * - **default** — renders the option's `response_text` for standard
 *   select-all type options.
 *
 * @param option - The selected option to display, including its `category_type`,
 *   `custom_values` (for contact/place/text_box types), and `response_text`
 *   (for default select-all options).
 */
export default function SafetyPlanSelectedOptionValue({
  option,
  activeRelapsePreventionPlaces = [],
}: SafetyPlanSelectedOptionValueProps) {
  const classes = useStyles()
  const type = option.category_type

  const renderRelapsePreventionPlaces = () => {
    if (activeRelapsePreventionPlaces.length === 0) {
      return null
    }

    return (
      <>
        {activeRelapsePreventionPlaces.map((place) => (
          <div className={classes.riskyPlaceCard} key={place.id.toString()}>
            <div className={classes.riskyPlaceIconContainer}>
              <FontAwesomeIcon
                icon={faLocationDot}
                className={classes.riskyPlaceIcon}
              />
            </div>
            <Typography className={classes.riskyPlaceText} component="div">
              {place.category}
            </Typography>
          </div>
        ))}
      </>
    )
  }

  const textAreaValues = () => {
    const values = option.custom_values as string[]
    return (
      <>
        {values.map((item: string, index: number) => (
          <div className={classes.textAreaCard} key={index.toString()}>
            <Typography className={classes.textAreaText} component="div">
              {item}
            </Typography>
          </div>
        ))}
      </>
    )
  }

  switch (type) {
    case "contact": {
      const contacts = option.custom_values as Contact[]
      return (
        <>
          {contacts.map((item: Contact, index: number) => (
            <div
              id="contacts"
              className={classes.option}
              key={index.toString()}
            >
              <Typography className={classes.optionContactTxt} component="div">
                {item.first} {item.last}
              </Typography>
              <Typography
                className={classes.optionContactPhoneTxt}
                component="div"
              >
                {item.phone}
              </Typography>
            </div>
          ))}
        </>
      )
    }

    case "place": {
      const places = option.custom_values as Place[]
      return (
        <>
          {places.map((item: Place, index: number) => (
            <div className={classes.option} key={index.toString()}>
              <Typography className={classes.optionTxt} component="div">
                {item.place}
              </Typography>
              <FontAwesomeIcon
                icon={faLocationDot}
                className={classes.plusColor}
              />
            </div>
          ))}
        </>
      )
    }

    case "text_box": {
      const values = option.custom_values as string[]
      return (
        <>
          {values.map((item: string, index: number) => (
            <div className={classes.option} key={index.toString()}>
              <Typography
                id={index.toString()}
                className={classes.optionTxt}
                component="div"
              >
                {item}
              </Typography>
            </div>
          ))}
        </>
      )
    }

    case "radio": {
      const val = option.id === "keepingMyselfSafe" ? false : true
      return (
        <div className={classes.radioContainer}>
          <fieldset
            role="radiogroup"
            aria-label="Keeping Myself Safe"
            style={{ border: "none", padding: 0, margin: 0 }}
            className={classes.radioOptions}
          >
            <label
              className={
                val ? classes.radioOptionSelected : classes.radioOption
              }
            >
              <input
                type="radio"
                name={`keeping-myself-safe-${option.id}`}
                value="yes"
                checked={val}
                readOnly
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: 0,
                  height: 0,
                }}
              />
              Yes
            </label>
            <label
              className={
                !val ? classes.radioOptionSelected : classes.radioOption
              }
            >
              <input
                type="radio"
                name={`keeping-myself-safe-${option.id}`}
                value="no"
                checked={!val}
                readOnly
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: 0,
                  height: 0,
                }}
              />
              No
            </label>
          </fieldset>
        </div>
      )
    }
    case "risky_place":
      return renderRelapsePreventionPlaces()
    case "safe_place":
      return renderRelapsePreventionPlaces()
    case "text_area":
      return textAreaValues()
    default:
      return (
        <div id={option.id} className={classes.option}>
          <Typography className={classes.optionTxt} component="div">
            {option.response_text}
          </Typography>
        </div>
      )
  }
}

const useStyles = makeStyles(() => ({
  option: {
    display: "flex",
    minHeight: "52px",
    padding: "16px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "4px",
    background: colors.safetyPlan.background,
  },
  optionTxt: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "20px",
    color: colors.safetyPlan.midnight,
    flex: "1 0 0",
    textAlign: "center",
  },
  optionContactTxt: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "20px",
    color: colors.safetyPlan.midnight,
    flex: "1 0 0",
  },
  optionContactPhoneTxt: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "20px",
    color: colors.safetyPlan.midnight,
  },
  plusColor: {
    color: colors.primary.shadow,
  },
  radioContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    borderRadius: "8px",
  },
  radioOptions: {
    display: "flex",
    height: "38px",
    padding: "2px",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "360px",
    background: "rgba(120, 120, 128, 0.12)",
  },
  radioOption: {
    display: "flex",
    height: "34px",
    padding: "3px 10px",
    alignItems: "center",
    flex: "1 0 0",
    borderRadius: "360px 360px 360px 360px",
    justifyContent: "center",
  },
  radioOptionSelected: {
    display: "flex",
    height: "34px",
    padding: "3px 10px",
    alignItems: "center",
    flex: "1 0 0",
    borderRadius: "360px 360px 360px 360px",
    justifyContent: "center",
    background: colors.neutral.white,
  },
  riskyPlaceCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    alignSelf: "stretch",
    minHeight: "72px",
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.neutral.white,
    boxSizing: "border-box",
  },
  riskyPlaceIconContainer: {
    display: "flex",
    width: "36px",
    height: "36px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    background: colors.tertiary.orange,
    flexShrink: 0,
  },
  riskyPlaceIcon: {
    color: colors.neutral.white,
    fontSize: "18px",
  },
  riskyPlaceText: {
    color: colors.safetyPlan.ink,
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: "24px",
    flex: "1 1 auto",
  },
  textAreaCard: {
    alignSelf: "stretch",
    padding: "20px",
    borderRadius: "8px",
    border: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.neutral.white,
    boxSizing: "border-box",
  },
  textAreaText: {
    color: colors.neutral.asphalt,
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "28px",
    whiteSpace: "pre-wrap",
  },
}))
